import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import {
  HttpClient,
  HttpHeaders,
  HttpEvent,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { NuclearTopicsDistrEnriched } from '../models/ai-model';
import { ActivePatientFieldsForAiAnalysis } from '../models/patient-model';
import { ErrorPanelService } from '../services/error-panel.service';

@Injectable({
  providedIn: 'root',
})
export class AiService {
  private apiUrl = environment.apiUrl;
  private numSpeakers: number = 2;
  private audioLanguage: string = 'it';

  constructor(
    private http: HttpClient,
    private errorPanelService: ErrorPanelService
  ) {}

  addHeader(): HttpHeaders {
    const token = sessionStorage.getItem('psicogeniusAccessToken');
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  getPatientFieldsForAiAnalysis(
    patientId: string
  ): Observable<ActivePatientFieldsForAiAnalysis> {
    const endpoint = this.apiUrl + '/patient/' + patientId + '/ai';
    const headers = this.addHeader();
    return this.http.get<ActivePatientFieldsForAiAnalysis>(endpoint, {
      headers,
    });
  }

  getActivePatientsFieldsForAiAnalysis(): Observable<
    ActivePatientFieldsForAiAnalysis[]
  > {
    const endpoint = this.apiUrl + '/patients/ai/active';
    const headers = this.addHeader();
    return this.http.get<ActivePatientFieldsForAiAnalysis[]>(endpoint, {
      headers,
    });
  }

  getPatDmr(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patients/' + patientId + '/patdmr';
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers, responseType: 'json' });
  }

  getPatSumm(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patients/' + patientId + '/patsumm';
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers, responseType: 'json' });
  }

  getGraphicsData(patientId: string): Observable<any> {
    const endpoint =
      this.apiUrl + '/patients/' + patientId + '/ai/categories-distributions';
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers });
  }

  getAppSumm(appointmentId: string): Observable<any> {
    const endpoint =
      this.apiUrl + '/appointments/' + appointmentId + '/ai/summary';
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers });
  }

  getNuclearTopicsDistrEnriched(
    appointmentId: string
  ): Observable<NuclearTopicsDistrEnriched> {
    const endpoint =
      this.apiUrl +
      '/appointments/' +
      appointmentId +
      '/ai/categories-distribution';
    const headers = this.addHeader();

    return this.http.get<NuclearTopicsDistrEnriched>(endpoint, { headers });
  }

  getAiTopicAppointment(appointmentId: string): Observable<any> {
    const endpoint =
      this.apiUrl + '/appointments/' + appointmentId + '/ai/topics';
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers });
  }

  getWordCloudData(appointmentId: string): Observable<any> {
    const endpoint =
      this.apiUrl + '/appointments/' + appointmentId + '/ai/wordcloud';
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers });
  }

  postAppointmentSummaryAndWordcloud(appointmentId: string): Observable<any> {
    const endpoint =
      this.apiUrl +
      '/appointments/' +
      appointmentId +
      '/ai/summary-and-wordcloud';
    const headers = this.addHeader();

    return this.http.post(endpoint, {}, { headers });
  }

  // apis for analysis ai page

  postAppointmentRecording(
    appointmentId: number,
    file: File
  ): Observable<HttpEvent<any>> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    const headers = this.addHeader();

    const req = new HttpRequest(
      'POST',
      `${this.apiUrl}/appointments/${appointmentId}/recording`,
      formData,
      {
        headers: headers,
        reportProgress: true,
        responseType: 'json',
      }
    );

    return this.http.request(req);
  }

  getAppointmentAiStatus(appointmentId: string): Observable<any> {
    const endpoint =
      this.apiUrl + '/appointments/' + appointmentId + '/ai/appointment-status';
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers });
  }

  getTranscription(appointmentId: string): Observable<Blob> {
    const endpoint = `${this.apiUrl}/appointments/${appointmentId}/ai/download-transcription`;
    const headers = this.addHeader();

    return this.http.get(endpoint, {
      headers: headers,
      params: { format: 'docx' },
      responseType: 'blob',
    });
  }

  deleteAIAppointmentOutputs(appointmentId: string): Observable<any> {
    const endpoint = this.apiUrl + '/appointments/' + appointmentId + '/ai';
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }

  setEventHandlersAndSendUploadRequest(
    signedUrl: string,
    file: File
  ): Observable<number | 'complete'> {
    return new Observable<number | 'complete'>((observer) => {
      // Direct upload to GCS using the signed URL
      const uploadRequest = new XMLHttpRequest();

      // Set OnProgress EventHandler (fired periodically when a request receives more data)
      uploadRequest.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          console.log('% progress: ', event.loaded, ' / ', event.total);
          const progress = Math.round((event.loaded / event.total) * 100);
          observer.next(progress);
        }
      };

      // Set Upload Completion Event Handler:
      uploadRequest.onload = () => {
        if (uploadRequest.status === 200) {
          observer.next('complete');
          observer.complete();
        } else {
          observer.error(new Error('Upload fallito'));
        }
      };

      // Set Upload Error Event Handler:
      uploadRequest.onerror = () => {
        observer.error(new Error('Upload fallito'));
      };

      // Send Upload Request:
      uploadRequest.open('PUT', signedUrl, true);
      uploadRequest.setRequestHeader('Content-Type', file.type);
      uploadRequest.send(file);

      // Probably needed for some cleanup logic
      return () => {
        uploadRequest.abort();
      };
    });
  }

  updateAppSumm(appointmentId: string, htmlContent: string): Observable<any> {
    return this.http.put(
      `${this.apiUrl}/appointments/${appointmentId}/ai/summary`,
      {
        content: htmlContent,
      },
      { headers: this.addHeader() }
    );
  }

  getNumSpeakers(): number {
    return this.numSpeakers;
  }

  setNumSpeakers(num: number) {
    this.numSpeakers = num;
  }

  getAudioLanguage(): string {
    return this.audioLanguage;
  }

  setAudioLanguage(lang: string) {
    this.audioLanguage = lang;
  }

  updatePatSumm(patientId: string, htmlContent: string): Observable<any> {
    return this.http.put(
      `${this.apiUrl}/patients/${patientId}/patsumm`,
      {
        content: htmlContent,
      },
      { headers: this.addHeader() }
    );
  }

  updatePatDmr(patientId: string, htmlContent: string): Observable<any> {
    return this.http.put(
      `${this.apiUrl}/patients/${patientId}/patdmr`,
      {
        content: htmlContent,
      },
      { headers: this.addHeader() }
    );
  }
}
